<template>
  <div>
    <el-drawer
      :title="`${itemId ? '编辑' : '新增'}发票报销`"
      custom-class="add-drawer"
      :visible.sync="drawer"
      :direction="$utils.isMobile() ? 'btt' : 'rtl'"
      :before-close="handleClose"
      :size="$utils.isMobile() ? '90%' : '38rem'"
      :wrapperClosable="false"
      :destroy-on-close="true"
    >
      <div class="form-content">
        <el-form
          ref="addForm"
          :label-position="$utils.isMobile() ? 'top' : 'left'"
          label-width="100px"
          :model="form"
          :rules="rules"
        >
          <el-form-item label="报销人" prop="userId">
            <el-select style="width: 100%" v-model="form.userId" placeholder="请选择" filterable clearable>
              <el-option v-for="item in saleUserList" :key="item.id" :label="item.name" :value="item.id">
              </el-option>
            </el-select>
          </el-form-item>

          <!-- ====== 新增模式：多选报销类型 ====== -->
          <template v-if="!itemId">
            <el-form-item label="报销类型" prop="invoiceTypeList">
              <el-select
                style="width: 100%"
                v-model="form.invoiceTypeList"
                placeholder="请选择（可多选）"
                multiple
                @change="onTypeChange"
              >
                <el-option v-for="item in invoiceTypeOptions" :key="item.id" :label="item.name" :value="item.id">
                </el-option>
              </el-select>
            </el-form-item>
          </template>

          <!-- ====== 编辑模式：单选报销类型 ====== -->
          <template v-else>
            <el-form-item label="报销类型" prop="invoiceType">
              <el-select style="width: 100%" v-model="form.invoiceType" placeholder="请选择">
                <el-option v-for="item in invoiceTypeOptions" :key="item.id" :label="item.name" :value="item.id">
                </el-option>
              </el-select>
            </el-form-item>
          </template>

          <el-form-item label="发生日期" prop="invoiceDate">
            <el-date-picker
              style="width: 100%"
              v-model="form.invoiceDate"
              type="date"
              placeholder="选择日期"
              value-format="yyyy-MM-dd"
              :picker-options="pickerOptions"
            >
            </el-date-picker>
          </el-form-item>

          <!-- ====== 新增模式：动态金额和附件 ====== -->
          <template v-if="!itemId">
            <div v-for="typeId in form.invoiceTypeList" :key="'type-' + typeId" class="type-section">
              <div class="type-section-header">
                <span class="type-section-tag" :class="'tag-type-' + typeId">
                  <i :class="getTypeIcon(typeId)"></i>
                  {{ getInvoiceTypeName(typeId) }}
                </span>
              </div>
              <el-form-item
                :label="getInvoiceTypeName(typeId) + '金额(元)'"
                :prop="'amountMap.' + typeId"
                :rules="[{ required: true, message: '请输入金额', trigger: 'blur' }]"
                label-width="165px"
              >
                <el-input
                  v-model="form.amountMap[typeId]"
                  placeholder="请输入金额"
                  @input="(val) => onAmountInput(val, typeId)"
                >
                  <template slot="prepend">￥</template>
                </el-input>
              </el-form-item>
              <el-form-item :label="getInvoiceTypeName(typeId) + '发票附件'" label-width="165px">
                <div class="upload-box">
                  <el-upload
                    action=""
                    :http-request="(opt) => uploadRequest(opt, typeId)"
                    :file-list="fileListMap[typeId] || []"
                    multiple
                    :show-file-list="false"
                    accept="image/*,.pdf"
                  >
                    <el-button size="small" type="primary">点击上传</el-button>
                  </el-upload>
                  <ul class="file-list" v-if="fileListMap[typeId] && fileListMap[typeId].length > 0">
                    <li v-for="(item, index) in fileListMap[typeId]" :key="item.uid">
                      <i class="el-icon-document"></i>
                      <span class="name" @click.stop="fileDownload(item)">{{ item.name }}</span>
                      <i class="el-icon-close close" @click.stop="fileRemove(typeId, index)"></i>
                    </li>
                  </ul>
                </div>
              </el-form-item>
            </div>
          </template>

          <!-- ====== 编辑模式：单条金额和附件 ====== -->
          <template v-else>
            <el-form-item label="金额(元)" prop="amount">
              <el-input v-model="form.amount" placeholder="请输入金额" @input="onAmountInputSingle">
                <template slot="prepend">￥</template>
              </el-input>
            </el-form-item>
            <el-form-item label="发票附件">
              <div class="upload-box">
                <el-upload action="" :http-request="(opt) => uploadRequest(opt, null)" :file-list="fileList" multiple :show-file-list="false" accept="image/*,.pdf">
                  <el-button size="small" type="primary">点击上传</el-button>
                  <div slot="tip" class="el-upload__tip">支持图片/PDF，单个文件不超过50MB</div>
                </el-upload>
                <ul class="file-list" v-if="fileList.length > 0">
                  <li v-for="(item, index) in fileList" :key="item.uid">
                    <i class="el-icon-document"></i>
                    <span class="name" @click.stop="fileDownload(item)">{{ item.name }}</span>
                    <i class="el-icon-close close" @click.stop="fileRemoveSingle(index)"></i>
                  </li>
                </ul>
              </div>
            </el-form-item>
          </template>

          <el-form-item label="关联拜访">
            <el-button type="text" @click="openVisitDialog">
              <i class="el-icon-plus" style="margin-right:2px"></i>选择拜访
            </el-button>
            <div v-if="form.visitList.length > 0" class="visit-tags">
              <span v-for="item in form.visitList" :key="item.id" class="visit-tag">
                <i class="el-icon-location-outline"></i>
                <span class="tag-content">{{ item.visitDay }} {{ item.sname }}</span>
                <i class="el-icon-close tag-close" @click.stop="removeVisit(item.id)"></i>
              </span>
            </div>
            <span v-else class="visit-empty">暂未关联</span>
          </el-form-item>
          <el-form-item label="备注">
            <el-input v-model="form.remark" placeholder="请输入备注信息" type="textarea" :rows="4" maxlength="500"></el-input>
          </el-form-item>
          <el-form-item label="">
            <el-button class="save-btn" @click="save" :loading="loadSave">保存</el-button>
          </el-form-item>
        </el-form>
      </div>
      <drawer-close
        v-if="!$utils.isMobile()"
        :visible="drawer"
        right="38rem"
        @close="handleClose"
      ></drawer-close>
    </el-drawer>
    <visit-select-dialog
      :visible.sync="visitDialogVisible"
      :userId="form.userId"
      :invoiceDate="form.invoiceDate"
      :selectedVisitIds="form.visitIdList"
      @select="handleVisitSelect"
    ></visit-select-dialog>
  </div>
</template>
<script>
import {
  saveInvoiceBatch,
  updateInvoice,
  findOneInvoiceById,
  uploadInvoiceFile,
  downInvoiceFile,
} from "@/api/invoice/index.js";
import { mapState } from "vuex";
import visitSelectDialog from "./visitSelectDialog.vue";
export default {
  components: { visitSelectDialog },
  props: {
    drawer: {
      type: Boolean,
      default: true,
    },
    itemId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      loadSave: false,
      visitDialogVisible: false,
      form: {
        // 编辑模式
        invoiceType: "",
        amount: "",
        // 新增模式
        invoiceTypeList: [],
        amountMap: {},
        // 共享字段
        userId: "",
        invoiceDate: "",
        visitIdList: [],
        visitList: [],
        remark: "",
      },
      invoiceTypeOptions: [
        { id: 1, name: "住宿费" },
        { id: 2, name: "交通费" },
        { id: 4, name: "客户维护费" },
        { id: 6, name: "服务费" },
        { id: 5, name: "其他" },
      ],
      rules: {
        invoiceType: [{ required: true, message: "请选择报销类型", trigger: "change" }],
        invoiceTypeList: [{ required: true, message: "请选择报销类型", trigger: "change" }],
        userId: [{ required: true, message: "请选择报销人", trigger: "change" }],
        invoiceDate: [{ required: true, message: "请选择发生日期", trigger: "change" }],
        amount: [{ required: true, message: "请输入金额", trigger: "blur" }],
      },
      // 编辑模式文件列表
      fileList: [],
      // 新增模式按类型分组的文件列表 { 1: [...], 2: [...] }
      fileListMap: {},
      pickerOptions: {
        disabledDate: (time) => {
          return time.getTime() > Date.now();
        },
      },
    };
  },
  watch: {
    drawer(val) {
      if (val && this.itemId) {
        this.loadSave = false;
        this.findOneInvoiceByIdApi();
      }
    },
  },
  computed: {
    ...mapState(["salePersonOption"]),
    saleUserList() {
      return this.salePersonOption.filter((item) => item.id !== "");
    },
  },
  methods: {
    // 报销类型名称
    getInvoiceTypeName(type) {
      let map = { 1: "住宿费", 2: "交通费", 4: "客户维护费", 6: "服务费", 5: "其他" };
      return map[type] || "--";
    },
    // 报销类型图标
    getTypeIcon(type) {
      let map = { 1: "el-icon-s-home", 2: "el-icon-truck", 4: "el-icon-office-building", 6: "el-icon-coin", 5: "el-icon-s-goods" };
      return map[type] || "el-icon-s-goods";
    },
    // 多选类型变化时，同步金额和文件列表
    onTypeChange(val) {
      let newMap = {};
      let newFileMap = {};
      val.forEach((typeId) => {
        newMap[typeId] = this.form.amountMap[typeId] || "";
        newFileMap[typeId] = this.fileListMap[typeId] || [];
      });
      this.form.amountMap = newMap;
      this.fileListMap = newFileMap;
    },
    // 新增模式：金额输入校验
    onAmountInput(val, typeId) {
      this.$set(this.form.amountMap, typeId, this.$utils.priceInput(val));
    },
    // 编辑模式：金额输入校验
    onAmountInputSingle(val) {
      this.form.amount = this.$utils.priceInput(val);
    },
    // 根据id查询详情
    findOneInvoiceByIdApi() {
      findOneInvoiceById({ id: this.itemId })
        .then((res) => {
          let data = res.data;
          this.form = {
            invoiceType: data.invoiceType,
            invoiceTypeList: [],
            amountMap: {},
            userId: data.userId || "",
            invoiceDate: data.invoiceDate,
            amount: data.amount,
            visitIdList: (data.visitList || []).map((item) => item.id),
            visitList: data.visitList || [],
            remark: data.remark || "",
          };
          this.fileList = (data.fileList || []).map((item) => {
            return {
              uid: item.id,
              name: item.fileName,
              path: item.filePath,
              id: item.id,
            };
          });
        })
        .catch((err) => {
          this.$MessageBox.MessageWarning(err.data.msg);
        });
    },
    // 文件上传
    uploadRequest({ file, onProgress, onSuccess, onError }, typeId) {
      let size = file.size / 1024 / 1024;
      if (size > 50) {
        this.$MessageBox.MessageWarning("上传失败！文件大小不得超过50M");
        return false;
      }
      const formData = new FormData();
      formData.append("file", file);
      let query = `?bizType=3`;
      if (this.itemId) {
        query += `&bizId=${this.itemId}`;
      }
      uploadInvoiceFile(formData, query, (event) => {
        let percent = Math.round((event.loaded * 100) / event.total);
        onProgress({ percent });
      })
        .then((res) => {
          onSuccess(res.data);
          let fileItem = {
            uid: file.uid,
            name: file.name,
            path: res.data.filePath,
            id: res.data.id,
          };
          if (typeId) {
            // 新增模式：按类型分组
            if (!this.fileListMap[typeId]) {
              this.$set(this.fileListMap, typeId, []);
            }
            this.fileListMap[typeId].push(fileItem);
          } else {
            // 编辑模式
            this.fileList.push(fileItem);
          }
        })
        .catch((err) => {
          onError(err);
          this.$MessageBox.MessageWarning(err.data.msg);
        });
    },
    // 新增模式：移除文件
    fileRemove(typeId, index) {
      this.fileListMap[typeId].splice(index, 1);
    },
    // 编辑模式：移除文件
    fileRemoveSingle(index) {
      this.fileList.splice(index, 1);
    },
    // 下载文件
    fileDownload({ id }) {
      downInvoiceFile({ fileId: id })
        .then((res) => {
          if (res.data.type === "application/json") {
            let blobReader = new Response(res.data).json();
            blobReader.then((item) => {
              this.$MessageBox.MessageWarning(item.msg);
            });
          } else {
            this.$utils.download(res);
          }
        })
        .catch((err) => {
          this.$MessageBox.MessageWarning(err.data.msg);
        });
    },
    // 打开关联拜访弹窗
    openVisitDialog() {
      if (!this.form.userId) {
        this.$MessageBox.MessageWarning("请先选择报销人");
        return;
      }
      if (!this.form.invoiceDate) {
        this.$MessageBox.MessageWarning("请先选择发生日期");
        return;
      }
      this.visitDialogVisible = true;
    },
    // 选择拜访回调
    handleVisitSelect(selection) {
      this.form.visitIdList = selection.map((item) => item.id);
      this.form.visitList = selection.map((item) => ({
        id: item.id,
        visitDay: item.visitDay,
        sname: item.sname,
      }));
    },
    // 移除单个关联拜访
    removeVisit(visitId) {
      this.form.visitIdList = this.form.visitIdList.filter((id) => id !== visitId);
      this.form.visitList = this.form.visitList.filter((item) => item.id !== visitId);
    },
    save() {
      this.loadSave = true;
      this.$refs.addForm.validate((valid) => {
        if (valid) {
          if (this.itemId) {
            // 编辑模式：单条保存
            let param = {
              id: this.itemId,
              invoiceType: this.form.invoiceType,
              userId: this.form.userId,
              invoiceDate: this.form.invoiceDate,
              amount: this.form.amount,
              visitIdList: this.form.visitIdList,
              remark: this.form.remark,
              fileList: this.fileList.map((item) => {
                return { id: item.id, fileName: item.name, filePath: item.path };
              }),
            };
            updateInvoice(param)
              .then(() => {
                this.$MessageBox.MessageSuccess("保存成功");
                this.$emit("updateList", "edit");
                this.handleClose();
                this.loadSave = false;
              })
              .catch((err) => {
                this.$MessageBox.MessageWarning(err.data.msg);
                this.loadSave = false;
              });
          } else {
            // 新增模式：批量保存
            let invoiceList = this.form.invoiceTypeList.map((typeId) => {
              return {
                invoiceType: typeId,
                amount: this.form.amountMap[typeId],
                fileList: (this.fileListMap[typeId] || []).map((item) => {
                  return { id: item.id, fileName: item.name, filePath: item.path };
                }),
              };
            });
            let param = {
              userId: this.form.userId,
              invoiceDate: this.form.invoiceDate,
              remark: this.form.remark,
              visitIdList: this.form.visitIdList,
              invoiceList: invoiceList,
            };
            saveInvoiceBatch(param)
              .then(() => {
                this.$MessageBox.MessageSuccess("保存成功");
                this.$emit("updateList", "add");
                this.handleClose();
                this.loadSave = false;
              })
              .catch((err) => {
                this.$MessageBox.MessageWarning(err.data.msg);
                this.loadSave = false;
              });
          }
        } else {
          this.loadSave = false;
          return;
        }
      });
    },
    handleClose() {
      this.form = {
        invoiceType: "",
        invoiceTypeList: [],
        amountMap: {},
        userId: "",
        invoiceDate: "",
        amount: "",
        visitIdList: [],
        visitList: [],
        remark: "",
      };
      this.fileList = [];
      this.fileListMap = {};
      this.$nextTick(() => {
        this.$refs.addForm.clearValidate();
        this.$emit("update:drawer", false);
      });
    },
  },
};
</script>
<style lang="scss" scoped>
/* 类型分区 */
.type-section {
  border: 1px solid #f0f2f5;
  border-radius: 10px;
  padding: 14px 12px 4px;
  margin-bottom: 16px;
  background: #fafbfc;
  .type-section-header {
    margin-bottom: 10px;
    .type-section-tag {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 3px 12px;
      border-radius: 14px;
      font-size: 0.82rem;
      font-weight: 600;
      i {
        font-size: 0.85rem;
      }
      &.tag-type-1 { background: #fff4e6; color: #ed8936; }
      &.tag-type-2 { background: #ebf8ff; color: #4299e1; }
      &.tag-type-4 { background: #f0fdf4; color: #22c55e; }
      &.tag-type-6 { background: #faf5ff; color: #a855f7; }
      &.tag-type-5 { background: #f7fafc; color: #718096; }
    }
  }
}
.upload-box {
  /deep/ .el-upload {
    width: 100%;
    text-align: left;
  }
  .el-upload__tip {
    margin-left: 10px;
  }
}
.file-list {
  width: 100%;
  list-style: none;
  li {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    color: #606266;
    margin-top: 5px;
    padding: 0 5px;
    line-height: 1.8;
    border-radius: 4px;
    cursor: pointer;
    .name {
      max-width: 400px;
      margin-left: 5px;
      margin-right: auto;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .close {
      cursor: pointer;
    }
    &:hover {
      color: $primary-btn-color;
      background-color: #f5f7fa;
    }
  }
}
.visit-empty {
  color: #c0c4cc;
  font-size: 0.85rem;
  margin-left: 0.3rem;
}
.visit-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}
.visit-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0.2rem 0.55rem;
  background: #ecf5ff;
  border: 1px solid #d9ecff;
  border-radius: 4px;
  font-size: 0.8rem;
  color: #409eff;
  line-height: 1.4;
  max-width: 100%;
  i {
    font-size: 0.8rem;
  }
  .tag-content {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 16rem;
  }
  .tag-close {
    cursor: pointer;
    font-size: 0.75rem;
    border-radius: 50%;
    background: rgba(64,158,255,0.15);
    width: 14px;
    height: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    &:hover {
      background: rgba(64,158,255,0.3);
    }
  }
}
.save-btn {
  width: 6.9rem;
  height: 2.38rem;
  background: $primary-btn-color;
  border: none;
  color: #fff;
  border-radius: 6px;
  display: inline-block;
  margin: 0.5rem 0 3rem;
  cursor: pointer;
}
</style>
